import React from "react";
import * as ReactDOMLegacy from "react-dom";
import { createRoot } from "react-dom/client";
import "./styles.css";

// The design-system bundle is a browser global build that reads window.React /
// window.ReactDOM at load time. Publishing this app's single React instance to
// those globals means the DS components and the app share one React (no
// duplicate-renderer hook errors), and the UI renders identically to the
// approved prototype.
/**
 * 🔑 **The `any` here was hiding a real mismatch, which is why it is worth
 * typing rather than suppressing.** `@types/react-dom` carries
 * `export as namespace ReactDOM`, so `window.ReactDOM` is ALREADY typed as the
 * legacy module — and what this app publishes is deliberately NOT that. It is a
 * HYBRID: legacy `react-dom` plus `createRoot`, which lives in
 * `react-dom/client` and is absent from the legacy type. The DS bundle reads
 * `window.ReactDOM.createRoot`, so the extra property is the whole point.
 *
 * So the shape is named, and the cast is a single one at a named boundary
 * rather than an `any` at each assignment. The OBJECT is still checked against
 * `DesignSystemGlobals` — `any` checked nothing.
 */
interface DesignSystemGlobals {
  React: typeof React;
  ReactDOM: typeof ReactDOMLegacy & { createRoot: typeof createRoot };
}

const dsGlobals = window as unknown as DesignSystemGlobals;
dsGlobals.React = React;
dsGlobals.ReactDOM = { ...ReactDOMLegacy, createRoot };

function loadDesignSystem(): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = import.meta.env.BASE_URL + "ds/_ds_bundle.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load the design-system bundle"));
    document.head.appendChild(s);
  });
}

// App is imported *after* the bundle loads, because the screens read the DS
// components from window.ArcRunDS at module scope.
// `/admin` is the admin console — a separate app on its own sign-in, not a
// screen of the reader app (Tom, 2026-10-02). Everything else is the reader app.
const isAdmin = window.location.pathname.replace(/\/+$/u, "") === "/admin";

loadDesignSystem()
  .then(() => (isAdmin ? import("./admin/AdminApp") : import("./App")))
  .then(async ({ default: Root }) => {
    // A13: while an administrator views a reader's account (read-only, with the
    // reader's consent), a banner sits above every reader screen.
    const Banner = isAdmin ? null : (await import("./ViewingBanner")).default;
    createRoot(document.getElementById("root")!).render(
      <>
        {Banner && <Banner />}
        <Root />
      </>,
    );
  })
  .catch((err) => {
    document.getElementById("root")!.textContent = String(err && err.message ? err.message : err);
  });
